/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

/* eslint-disable */

/**
 * Call requestIdleCallback in supported browsers.
 *
 * https://developers.google.com/web/updates/2015/08/using-requestidlecallback
 * http://caniuse.com/#feat=requestidlecallback
 */
export const requestIdleCallback = (fn) => {
    if ('requestIdleCallback' in window) {
        return window.requestIdleCallback(fn);
    } 
        return setTimeout(() => fn(), 1);
    
};

export const watchOnlineStatus = (callback, win = window) => {
    const off = () => callback(false);
    const on = () => callback(true);
    win.addEventListener('offline', off);
    win.addEventListener('online', on);
    const unsubscribe = () => {
        win.removeEventListener('offline', off);
        win.removeEventListener('online', on);
    };
    return unsubscribe;
};

/**
 * Compares the primary fields of two address objects to determine if they match.
 * @param {Object} addr1
 * @param {Object} addr2
 * @returns {boolean}
 */
export const isMatchingAddress = (addr1, addr2) => {
    const normalize = (addr) => {
        // eslint-disable-next-line no-unused-vars
        const { id, addressId, _type, preferred, creationDate, lastModified, ...normalized } = addr;
        return normalized;
    };
    return shallowEquals(normalize(addr1), normalize(addr2));
};

/**
 * Performs a shallow comparison on two objects
 * @param {Object} a
 * @param {Object} b
 * @returns {boolean}
 */
export const shallowEquals = (a, b) => {
    for (const key in a) {
        if (!(key in b) || a[key] !== b[key]) {
            return false;
        }
    }
    for (const key in b) {
        if (!(key in a) || a[key] !== b[key]) {
            return false;
        }
    }
    return true;
};

/**
 * No operation function. You can use this
 * empty function when you wish to pass
 * around a function that will do nothing.
 * Usually used as default for event handlers.
 */
export const noop = () => {};

/**
 * Flattens a tree data structure into an array.
 * @param {*} node
 * @returns
 */
export const flatten = (node, key = 'children') => {
    const children = (node[key] || []).reduce((a, b) => {
        return Array.isArray(b[key]) && !!b[key].length
            ? { ...a, ...flatten(b, key) }
            : { ...a, [b.id]: b };
    }, {});

    return {
        [node.id]: node,
        ...children,
    };
};

/**
 * Check the current execution environment
 * is client side or server side
 * @returns Boolean
 */
export const isServer = typeof window === 'undefined';

/**
 * retrieves an item from session storage
 * @param {string} key
 * @returns JSON | undefined
 */
export const getSessionJSONItem = (key) => {
    if (isServer) {
        return undefined;
    }
    const item = window.sessionStorage.getItem(key);
    if (item) {
        return JSON.parse(item);
    } 
        return undefined;
    
};
/**
 * sets an item in session storage
 * @param {string} key
 * @param {string} value
 */
export const setSessionJSONItem = (key, value) => {
    window.sessionStorage.setItem(key, JSON.stringify(value));
};

/**
 * clears an item in session storage
 * @param {string} key
 */
export const clearSessionJSONItem = (key) => {
    window.sessionStorage.removeItem(key);
};

/**
 * bolds a substring of a string by adding <b> tags
 * @param {string} str
 * @param {string} substr
 * @returns stringified HTML Node
 */
export const boldString = (str, substr) => {
    return str.replace(RegExp(substr, 'g'), `<b>${substr}</b>`);
};

/**
 * Capitalizes the words in a string
 * @param {string} text
 * @returns capitalized text
 */
export const capitalize = (text) => {
    return text
        .toLowerCase()
        .split(' ')
        .map((s) => s.charAt(0).toUpperCase() + s.substring(1))
        .join(' ');
};


/**
 * Set value of preview flag based on provided SFCC data
 * @param {string} text
 * @returns {boolean} value
 */
export const setPreviewFlag = (sfccData) => {
    let flag = false;
    const previewWhiteListedUrl = ['aa-test.mobify-storefront.com'];
    previewWhiteListedUrl.forEach((url) => {
      if(sfccData.wizardUrl && sfccData.wizardUrl.includes(url)) {
          flag = true;
        }
    })
    return flag; 
};

/**
 * 
 * @param {string} locales 
 * @returns {string} 
 */
export const getLocale = (locales) => {
    let sfccLocale = locales?.locale??undefined;
    if(typeof sfccLocale !== 'string' && typeof locales[0] === 'string') {
      sfccLocale = locales[0];
    }
    // Handle .html and long URL confusing react routing
    if(sfccLocale && (sfccLocale.includes('.html') || sfccLocale.includes('/') )) {
        let tempArray = sfccLocale.split('/');
        sfccLocale = tempArray[2];
    }

    let locale = 'en-US';

    if(sfccLocale) {
        sfccLocale = sfccLocale.toLowerCase();
        const localeMapping = [{'en-us': 'en-US'},{'ja-jp': 'ja-JP' },{'en-au': 'en-AU'},{'en-nl': 'en-NL'},{'nl-nl':'nl-NL'}];
        localeMapping.forEach((localeMap) => {
          if(localeMap[sfccLocale]) {
            locale = localeMap[sfccLocale];
          }
        });
    }

    return locale;
}

/***
 * Get Translated Pronation Text
 * @param {string} facets
 * @param {string} resultsPageData
 * @returns {string} value 
 */
export const getTranslatedPronationText = (facets, resultsPageData) => {
    let translatedText = '';
    facets.map((madeForFacet) => {
      if(madeForFacet.facet === 'pronation') {
        const madeForIconData = resultsPageData?.madeFor?.iconsCollection?.items.filter((iconData) => (
          madeForFacet.facet === iconData.facet?.toLowerCase() &&
          madeForFacet.value === iconData.value?.toLowerCase()
        ));
        if(madeForIconData){
            translatedText = madeForIconData[0]?.title ?? '';
        }
      }
    });
    return translatedText;
};

/**
 * @param {String} wizards
 * @param {String} wizardId
 * @param {Array} questionsSuperSet
 * @returns {Array} 
 */
export const getFilteredQuestion = (wizards, wizardId, questionsSuperSet) => {
    let finalQuestions = []
    const wizard = wizards[wizardId];
    let stepsCollectionId = [];
    wizard?.stepsCollection?.items?.forEach(item => {
        stepsCollectionId.push(item?.sys?.id);
    })

    stepsCollectionId.forEach((step) => {
        const question = questionsSuperSet[step];
        if(question) {
          finalQuestions.push(question);
        }
    });

    return finalQuestions;
}

/**
 * @param {String} wizard
 * @param {Object} sfccData
 * @returns {String}  
 */
export const getAlgoliaIndex = (wizard, sfccData) => {
    let algoliaIndex
    const environmentMapping = sfccData?.sfcc_environment;
    if (environmentMapping === 'prod_sfcc') {
      algoliaIndex = wizard.indexes.prod;
    } else if (environmentMapping === 'stg_sfcc') {
      algoliaIndex = wizard.indexes.stg;
    } else if (environmentMapping === 'dev_sfcc') {
      algoliaIndex = wizard.indexes.dev;
    } else if (environmentMapping === 'sandbox_sfcc') {
      algoliaIndex = wizard.indexes.sandbox;
    } else {
      algoliaIndex = wizard.indexes.dev;
    }
    return algoliaIndex;
};